import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"

import { InfoBlock } from "../../components/containers/InfoBlock"
import { Report } from "../../types/generated/api/Report"
import { ReportContainer } from "../Insights"

export type ReportV1Props = {
  reportData: Report
  dataSetName?: string
  feedbackColumn?: string | null
  predictionDateRange?: ReactElement
}

/**
 * ReportV1 Component
 *
 * This component renders a section displaying information about a simulation report.
 * It includes details such as the dataset name, feedback column, and prediction date range,
 * and utilizes the ReportContainer to render insights based on the report data.
 *
 * @param {ReportV1Props} props - The properties for the component, including:
 *   - reportData: The report data object containing report details.
 *   - dataSetName: Name of the dataset or "Live Predictions" if undefined.
 *   - feedbackColumn: The name of the feedback column or "No column selected" if not provided.
 *   - predictionDateRange: Optional React element representing the prediction date range.
 *
 * @returns {ReactElement} A React element representing the rendered simulation report details.
 */

export function ReportV1(props: Readonly<ReportV1Props>): ReactElement {
  const { reportData, feedbackColumn, dataSetName, predictionDateRange } = props

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationReport.reportV1",
  })

  return (
    <Grid container item height={"100%"} flexDirection={"column"} flexWrap={"nowrap"}>
      <Grid container mb={2} display={"flex"} spacing={2}>
        <Grid item xs={12} lg={4}>
          <InfoBlock
            textToolTip={dataSetName ?? t("livePredictions")}
            type="default"
            title={t("dataset")}
            text={dataSetName ?? t("livePredictions")}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <InfoBlock type="default" title={t("feedbackColumn")} text={feedbackColumn ?? t("noColumnSelected")} />
        </Grid>
        {predictionDateRange && (
          <Grid item xs={12} lg={4}>
            <InfoBlock title={t("dateRange")} text={predictionDateRange} type="default" />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} pb={2}>
        <ReportContainer uuid={reportData.uuid} reportType="simulation" feedbackColumn={feedbackColumn} />
      </Grid>
    </Grid>
  )
}
